import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sun, Moon, Menu, LogOut, LayoutDashboard, User } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { KaffaLogo } from "./KaffaLogo";
import { KAFFA } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { AlertsBell } from "./AlertsBell";

const LANG_OPTIONS = [
  { value: "pt-BR", label: "PT-BR" },
  { value: "en", label: "EN" },
  { value: "es", label: "ES" },
  { value: "fr", label: "FR" },
  { value: "pl", label: "PL" },
  { value: "ar", label: "العربية" },
  { value: "zh", label: "中文" },
  { value: "ja", label: "日本語" },
];

const CURRENCY_OPTIONS = [
  { value: "USD", label: "USD" },
  { value: "BRL", label: "BRL" },
  { value: "EUR", label: "EUR" },
];

export const Header = () => {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const { locale, setLocale, currency, setCurrency, t } = useLocale();
  const navigate = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const dashboardPath = () => {
    if (!user) return "/login";
    if (user.role === "admin") return "/admin";
    if (user.role === "cooperative") return "/cooperative";
    return "/buyer";
  };

  const NavLinks = ({ onClick, vertical = false }) => (
    <>
      <Link
        to="/"
        onClick={onClick}
        className={`${vertical ? "py-3 text-base" : "px-4 py-2 text-sm"} font-medium tracking-tight hover:text-accent transition-colors ${
          loc.pathname === "/" ? "text-foreground" : "text-muted-foreground"
        }`}
      >
        {t("nav.home")}
      </Link>
      <Link
        to="/marketplace"
        onClick={onClick}
        data-testid={vertical ? undefined : KAFFA.navMarketplace}
        className={`${vertical ? "py-3 text-base" : "px-4 py-2 text-sm"} font-medium tracking-tight hover:text-accent transition-colors ${
          loc.pathname.startsWith("/marketplace") || loc.pathname.startsWith("/lot") ? "text-foreground" : "text-muted-foreground"
        }`}
      >
        {t("nav.marketplace")}
      </Link>
      <Link
        to="/tours"
        onClick={onClick}
        className={`${vertical ? "py-3 text-base" : "px-4 py-2 text-sm"} font-medium tracking-tight hover:text-accent transition-colors ${
          loc.pathname.startsWith("/tours") ? "text-foreground" : "text-muted-foreground"
        }`}
      >
        {t("nav.tours")}
      </Link>
      {user && (
        <Link
          to={dashboardPath()}
          onClick={onClick}
          className={`${vertical ? "py-3 text-base" : "px-4 py-2 text-sm"} font-medium tracking-tight hover:text-accent transition-colors ${
            loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/cooperative") || loc.pathname.startsWith("/buyer") ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          {t("nav.dashboard")}
        </Link>
      )}
    </>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/80 glass-header">
      <div className="mx-auto max-w-[1600px] flex items-center justify-between px-3 sm:px-8 h-14 sm:h-16 gap-2">
        <Link to="/" data-testid={KAFFA.headerLogo} className="hover:opacity-80 transition-opacity shrink-0">
          <KaffaLogo />
        </Link>

        {/* Desktop nav (md+) */}
        <nav className="hidden md:flex items-center gap-1">
          <NavLinks />
        </nav>

        {/* Right side cluster */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Language — full width on desktop, icon trigger on mobile */}
          <Select value={locale} onValueChange={setLocale}>
            <SelectTrigger
              data-testid={KAFFA.langSelect}
              className="h-8 w-[68px] sm:w-[80px] rounded-none border-border text-[11px] sm:text-xs font-mono-data uppercase tracking-wider bg-transparent px-2 [&_svg]:hidden sm:[&_svg]:block"
              aria-label="Language"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANG_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Currency */}
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger
              data-testid={KAFFA.currencySelect}
              className="h-8 w-[62px] sm:w-[78px] rounded-none border-border text-[11px] sm:text-xs font-mono-data uppercase tracking-wider bg-transparent px-2 [&_svg]:hidden sm:[&_svg]:block"
              aria-label="Currency"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Theme — hidden on mobile, available in drawer */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            data-testid={KAFFA.themeToggle}
            className="hidden sm:inline-flex rounded-none h-8 w-8 hover:bg-secondary"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          {/* Alerts (auth) */}
          {user && user !== false && <AlertsBell />}

          {/* User menu / Login / Register — desktop */}
          {user && user !== false ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  data-testid={KAFFA.userMenu}
                  className="rounded-none h-8 px-2 sm:px-3 hover:bg-secondary border border-border gap-1.5 sm:gap-2"
                >
                  <User className="h-3.5 w-3.5" />
                  <span className="text-xs font-mono-data uppercase tracking-wider hidden sm:inline max-w-[120px] truncate">
                    {user.name?.split(" ")[0] || user.email}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none w-56">
                <DropdownMenuLabel className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {user.role} {user.status && user.status !== "active" ? `· ${user.status}` : ""}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(dashboardPath())}>
                  <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => { await logout(); navigate("/"); }}
                  data-testid={KAFFA.logoutBtn}
                  className="text-destructive"
                >
                  <LogOut className="h-4 w-4 mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : user === false ? (
            <>
              {/* Sign in: visible from sm up — on xs it lives in the drawer */}
              <Link to="/login" data-testid={KAFFA.navLogin} className="hidden sm:inline-block">
                <Button variant="ghost" className="h-8 rounded-none hover:bg-secondary text-xs uppercase tracking-wider">
                  {t("nav.login")}
                </Button>
              </Link>
              <Link to="/register" data-testid={KAFFA.navRegister} className="hidden sm:inline-block">
                <Button className="h-8 rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-xs uppercase tracking-wider">
                  {t("nav.register")}
                </Button>
              </Link>
            </>
          ) : null}

          {/* Mobile menu trigger (xs/sm) */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-trigger"
                className="md:hidden rounded-none h-8 w-8 hover:bg-secondary"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[320px] rounded-none border-l border-border p-0">
              <div className="flex items-center px-5 h-14 border-b border-border">
                <KaffaLogo />
              </div>
              <nav className="flex flex-col px-5 py-4 border-b border-border">
                <NavLinks onClick={() => setMobileOpen(false)} vertical />
              </nav>
              <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                <span className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {t("settings.theme")}
                </span>
                <Button
                  variant="outline"
                  onClick={toggle}
                  className="rounded-none h-8 px-3 text-xs uppercase tracking-wider gap-2"
                >
                  {theme === "dark" ? (<><Sun className="h-3.5 w-3.5" /> Light</>) : (<><Moon className="h-3.5 w-3.5" /> Dark</>)}
                </Button>
              </div>
              {user === false && (
                <div className="px-5 py-4 flex flex-col gap-2">
                  <Link to="/login" onClick={() => setMobileOpen(false)}>
                    <Button variant="outline" className="w-full h-10 rounded-none text-xs uppercase tracking-wider">
                      {t("nav.login")}
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)}>
                    <Button className="w-full h-10 rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-xs uppercase tracking-wider">
                      {t("nav.register")}
                    </Button>
                  </Link>
                </div>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
